import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileText, Table2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { InfoNotesPage } from "./InfoNotesPage";
import { InfoTablePage } from "./InfoTablePage";

const FIXED = [["platforms", "المنصات"], ["stats", "الإحصائيات"], ["branches", "الفروع"]];
const TEMPLATE = [{ key: "name", label: "البيان", type: "text" }, { key: "expiry", label: "تاريخ الانتهاء", type: "expiry" }, { key: "note", label: "ملاحظات", type: "text" }];

const AddPageDialog = ({ open, onOpenChange, page, onSaved }) => {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("notes");
  useEffect(() => { if (open) { setTitle(page?.title || ""); setKind(page?.kind || "notes"); } }, [open, page]);
  const save = async () => {
    try {
      const r = page ? await api.updateInfoPage(page.id, { ...page, title }) : await api.createInfoPage({ title, kind, columns: kind === "table" ? TEMPLATE : [] });
      onSaved(r); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-sm" data-testid="info-page-dialog">
        <DialogHeader className="text-right"><DialogTitle>{page ? "تعديل الصفحة" : "صفحة جديدة"}</DialogTitle><DialogDescription>اختر اسم الصفحة ونوعها.</DialogDescription></DialogHeader>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: أرقام التواصل، السجلات التجارية" data-testid="info-page-title" />
        {!page && (
          <div className="grid grid-cols-2 gap-2">
            {[["notes", "ملاحظات ومرفقات", FileText], ["table", "جدول سجلات (مع عدّاد انتهاء)", Table2]].map(([k, l, I]) => (
              <button key={k} type="button" onClick={() => setKind(k)} data-testid={`info-page-kind-${k}`}
                className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-xs font-semibold ${kind === k ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}><I className="h-5 w-5" />{l}</button>
            ))}
          </div>
        )}
        <Button disabled={!title.trim()} onClick={save} data-testid="info-page-save">حفظ</Button>
      </DialogContent>
    </Dialog>
  );
};

export const InfoHub = ({ canEdit, platforms, stats, branches }) => {
  const [tab, setTab] = useState(() => sessionStorage.getItem("wz_info_tab") || "platforms");
  const [pages, setPages] = useState([]);
  const [dlg, setDlg] = useState({ open: false, page: null });
  const load = useCallback(() => api.infoPages().then(setPages).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { sessionStorage.setItem("wz_info_tab", tab); }, [tab]);
  const cur = pages.find((p) => p.id === tab);
  const savePage = async (p) => { const r = await api.updateInfoPage(p.id, p); setPages((ps) => ps.map((x) => (x.id === r.id ? r : x))); };
  const del = async (p) => {
    if (!window.confirm(`حذف صفحة «${p.title}»؟`)) return;
    await api.deleteInfoPage(p.id); setPages((ps) => ps.filter((x) => x.id !== p.id)); setTab("platforms");
  };
  const fixed = { platforms, stats, branches }[tab];
  return (
    <div className="space-y-5" data-testid="info-hub">
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="info-tabs">
        {[...FIXED, ...pages.map((p) => [p.id, p.title])].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`info-tab-${k}`}
            className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
        {canEdit && <button type="button" onClick={() => setDlg({ open: true, page: null })} data-testid="info-tab-add"
          className="flex shrink-0 items-center gap-1 rounded-full border border-dashed border-blue-400 px-3 py-1.5 text-sm font-semibold text-blue-700"><Plus className="h-4 w-4" /> صفحة</button>}
      </div>
      {fixed || (cur ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-lg font-bold text-slate-800">{cur.title}</h2>
            {canEdit && <>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDlg({ open: true, page: cur })} data-testid="info-page-rename"><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(cur)} data-testid="info-page-delete"><Trash2 className="h-4 w-4" /></Button>
            </>}
          </div>
          {cur.kind === "table" ? <InfoTablePage key={cur.id} page={cur} canEdit={canEdit} onSave={savePage} />
            : <InfoNotesPage key={cur.id} page={cur} canEdit={canEdit} onSave={savePage} />}
        </div>
      ) : platforms)}
      <AddPageDialog open={dlg.open} page={dlg.page} onOpenChange={(v) => setDlg((d) => ({ ...d, open: v }))}
        onSaved={(r) => { setPages((ps) => (ps.some((x) => x.id === r.id) ? ps.map((x) => (x.id === r.id ? r : x)) : [...ps, r])); setTab(r.id); }} />
    </div>
  );
};
